/**
 * Unified File Restrictions Configuration
 *
 * Uses the most restrictive limits across all platforms to ensure
 * files are accepted by any channel (WhatsApp, Shopee, Lazada, TikTok).
 */

export type FileCategory = "image" | "video" | "audio" | "document" | "sticker"

export type ChannelType = "whatsapp" | "shopee" | "lazada" | "tiktok" | "tokopedia"

/**
 * Unified size limits (most restrictive across all platforms)
 * All values in MB
 */
export const FILE_SIZE_LIMITS: Record<FileCategory, number> = {
  image: 5,      // WhatsApp/Shopee/Lazada/TikTok all use 5MB
  video: 16,     // WhatsApp limit (only WhatsApp supports video in chat)
  audio: 16,     // WhatsApp limit (only WhatsApp supports audio in chat)
  document: 100, // WhatsApp limit (only WhatsApp supports documents in chat)
  sticker: 0.5,  // 500KB for stickers
}

/**
 * Supported file extensions by category
 * Uses formats supported by ALL platforms where applicable
 */
export const SUPPORTED_EXTENSIONS: Record<FileCategory, string[]> = {
  image: ["jpeg", "jpg", "png"],
  video: ["mp4", "3gp"],
  audio: ["aac", "mp4", "mpeg", "amr", "ogg"],
  document: [
    "pdf",
    "doc", "docx",
    "xls", "xlsx",
    "ppt", "pptx",
    "txt", "csv", "rtf",
  ],
  sticker: ["webp"],
}

/**
 * MIME type subtypes that map to each category
 */
export const MIME_TYPE_MAPPING: Record<FileCategory, string[]> = {
  image: ["jpeg", "png", "jpg"],
  video: ["mp4", "3gp", "3gpp"],
  audio: ["aac", "mp4", "mpeg", "amr", "ogg", "audio/mpeg", "audio/mp4"],
  document: [
    "pdf",
    "plain", "rtf",
    "msword",
    "vnd.ms-excel",
    "vnd.ms-powerpoint",
    "vnd.openxmlformats-officedocument.wordprocessingml.document",
    "vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "vnd.openxmlformats-officedocument.presentationml.presentation",
    "comma-separated-values", "csv",
  ],
  sticker: ["webp"],
}

/**
 * Which file categories each platform supports
 */
export const PLATFORM_SUPPORT: Record<ChannelType, FileCategory[]> = {
  whatsapp: ["image", "video", "audio", "document", "sticker"],
  shopee: ["image", "sticker"],
  lazada: ["image"],
  tiktok: ["image"],
  tokopedia: ["image"],
}

/**
 * Human-readable format strings for UI display
 */
export const FORMAT_DISPLAY: Record<FileCategory, string> = {
  image: "JPEG, PNG",
  video: "MP4, 3GP",
  audio: "AAC, MP3, OGG, AMR",
  document: "PDF, DOC, XLS, PPT, TXT",
  sticker: "WebP",
}

/**
 * Error codes for validation failures
 */
export const ValidationError = {
  INVALID_TYPE: "InvalidType",
  MAX_SIZE_EXCEEDED: "MaxSize",
  INVALID_FORMAT: "InvalidFiletype",
  TYPE_NOT_SUPPORTED: "TypeNotSupported", // File type not supported on this platform
} as const

export type ValidationErrorType = typeof ValidationError[keyof typeof ValidationError]

/**
 * Get supported file categories for a channel
 */
export function getSupportedCategories(channel?: string): FileCategory[] {
  if (!channel) return ["image", "video", "audio", "document", "sticker"]
  const normalized = channel.toLowerCase() as ChannelType
  return PLATFORM_SUPPORT[normalized] ?? ["image"]
}

/**
 * Check if a file category is supported on a channel
 */
export function isCategorySupported(category: FileCategory, channel?: string): boolean {
  const supported = getSupportedCategories(channel)
  return supported.includes(category)
}

/**
 * Get size limit for a file category in bytes
 */
export function getSizeLimitBytes(category: FileCategory): number {
  return FILE_SIZE_LIMITS[category] * 1024 * 1024
}

/**
 * Get size limit for a file category in MB
 */
export function getSizeLimitMB(category: FileCategory): number {
  return FILE_SIZE_LIMITS[category]
}

/**
 * Format bytes to human-readable string
 */
export function formatFileSize(bytes: number, decimals = 2): string {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`
}

/**
 * Get restriction summary for UI display
 */
export function getRestrictionSummary(channel?: string): string {
  const categories = getSupportedCategories(channel)

  const parts = categories.map(cat => {
    const formats = FORMAT_DISPLAY[cat]
    const size = FILE_SIZE_LIMITS[cat]
    return `${cat.charAt(0).toUpperCase() + cat.slice(1)}: ${formats} (max ${size}MB)`
  })

  return parts.join(" | ")
}

/**
 * Get simple supported types string for UI
 */
export function getSupportedTypesString(channel?: string): string {
  const categories = getSupportedCategories(channel)
  return categories.map(cat => cat.charAt(0).toUpperCase() + cat.slice(1)).join(", ")
}
